/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import jwt from 'jwt-decode'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {login, resendVerification} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {UserModel} from '../core/_models'
import {Toast} from '../../../../ui-kit'
import {useLayout} from '../../../../_metronic/layout/core'

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  username: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const {config, setLayoutType, setLayout} = useLayout()
  const [isUnverified, setIsUnverified] = useState(false)
  const [unverifiedEmail, setUnverifiedEmail] = useState('')
  const [resending, setResending] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.username, values.password)
        saveAuth(auth)
        const user: UserModel = jwt(auth.data.access_token)
        setCurrentUser(user)

        const isUserRoleHasEconomy = !!user.economy

        if (isUserRoleHasEconomy) {
          setLayoutType('light-header')
          setLayout({
            ...config,
            app: {
              ...config.app,
              header: {
                ...config.app?.header,
                containerClass: 'd-flex align-items-stretch justify-content-between container',
              },
            },
          })
        } else {
          setLayoutType('light-sidebar')
          setLayout({
            ...config,
            app: {
              ...config.app,
              header: {
                ...config.app?.header,
                containerClass: 'd-flex align-items-stretch justify-content-between',
              },
            },
          })
        }

        // const role = user.role
        // switch (role) {
        //   case 'cambodia_member':
        //   case 'bangladesh_member':
        //     setLayoutType('light-header')
        //     setLayout({
        //       ...config,
        //       app: {
        //         header: {
        //           containerClass: 'd-flex align-items-stretch justify-content-between container',
        //         },
        //       },
        //     })
        //     break
        //   default:
        //     setLayoutType('light-sidebar')
        //     setLayout({
        //       ...config,
        //       app: {
        //         header: {
        //           containerClass: 'd-flex align-items-stretch justify-content-between',
        //         },
        //       },
        //     })
        // }
      } catch (error: any) {
        console.error(error)
        const errorData = error.response?.data
        if (errorData?.is_unverified) {
          setIsUnverified(true)
          setUnverifiedEmail(errorData.email)
          setStatus(errorData.data)
        } else {
          Toast(
            'Something Went Wrong',
            'danger',
            errorData?.data || 'The login details are incorrect'
          )
          setStatus('The login details are incorrect')
        }
        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleResend = async () => {
    if (!unverifiedEmail) return
    setResending(true)
    try {
      await resendVerification(unverifiedEmail)
      Toast('Verification email sent!', 'success', '')
      setIsUnverified(false)
    } catch (error: any) {
      console.error(error)
      Toast('Failed to resend verification email', 'danger', error.response?.data?.message)
    } finally {
      setResending(false)
    }
  }

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
          {isUnverified && (
            <div className='mt-3'>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={handleResend}
                disabled={resending}
              >
                {!resending && <span className='indicator-label'>Resend Verification Email</span>}
                {resending && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email or Username</label>
        <input
          placeholder='Email or Username'
          {...formik.getFieldProps('username')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.username && formik.errors.username},
            {
              'is-valid': formik.touched.username && !formik.errors.username,
            }
          )}
          type='username'
          name='username'
          autoComplete='off'
        />
        {formik.touched.username && formik.errors.username && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.username}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <input
          placeholder='Password'
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        {/* begin::Link */}
        <Link to='/login/forgot-password' className='link-primary'>
          Forgot Password ?
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-4'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Continue</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>

      {/* end::Action */}
    </form>
  )
}
